import type { Shell } from '@/api.ts';

export type NavItem = Shell['config']['nav'][number];
export type Mount = Shell['config']['mounts'][number];
export type Region = Mount['region'];

export const REGIONS: readonly Region[] = ['header', 'main', 'sidebar'];

/**
 * A slot name addresses a fragment, so it is bound by the gateway's own id rule — not by
 * anything this form invented. A slot that cannot be a fragment id is a slot nothing will ever
 * fill, and the control plane would reject the manifest later anyway.
 */
const SLOT = /^[a-z0-9][a-z0-9-]{0,63}$/;

export interface FieldError {
  /** `nav.1.href` — the path the message belongs to, so the form can put it next to the input. */
  path: string;
  message: string;
}

/**
 * Everything wrong with a shell's nav and mounts, as a flat list.
 *
 * Pure, and returning *all* the problems rather than the first: a form that reveals one error at
 * a time turns a two-minute edit into five round trips. The control plane validates
 * independently — this exists to answer sooner, not to be trusted.
 */
export function validateShellConfig(nav: NavItem[], mounts: Mount[]): FieldError[] {
  const errors: FieldError[] = [];

  nav.forEach((item, index) => {
    if (!item.label.trim()) errors.push({ path: `nav.${index}.label`, message: 'A label is required.' });
    if (!item.href.trim()) {
      errors.push({ path: `nav.${index}.href`, message: 'A link is required.' });
    } else if (!item.href.startsWith('/') && !/^https?:\/\//.test(item.href)) {
      errors.push({
        path: `nav.${index}.href`,
        message: 'Use a path beginning with / or a full http(s) URL.',
      });
    }
  });

  const seen = new Map<string, number>();
  mounts.forEach((mount, index) => {
    const slot = mount.slot.trim();
    if (!slot) {
      errors.push({ path: `mounts.${index}.slot`, message: 'A slot name is required.' });
    } else if (!SLOT.test(slot)) {
      errors.push({
        path: `mounts.${index}.slot`,
        message: 'Lowercase letters, digits and hyphens only — it has to be a valid fragment id.',
      });
    } else if (seen.has(slot)) {
      // Two slots with one name is not a validation nicety: `<fragment-slot name>` is how the
      // gateway matches a fragment, so the second one silently never fills.
      errors.push({
        path: `mounts.${index}.slot`,
        message: `Already mounted in row ${seen.get(slot)! + 1}. A slot name can only appear once.`,
      });
    } else {
      seen.set(slot, index);
    }

    if (mount.src && !mount.src.startsWith('/')) {
      errors.push({ path: `mounts.${index}.src`, message: 'A source path begins with /.' });
    }
    if (!REGIONS.includes(mount.region)) {
      errors.push({ path: `mounts.${index}.region`, message: 'Pick a region.' });
    }
  });

  return errors;
}

export function errorFor(errors: FieldError[], path: string): string | undefined {
  return errors.find((error) => error.path === path)?.message;
}

/**
 * Moves an item, returning a new array.
 *
 * Order is meaningful in both lists — the shell renders nav items and mounts in the order given —
 * so reordering is an edit, not a display preference.
 */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

export function removeAt<T>(items: T[], index: number): T[] {
  return items.filter((_, position) => position !== index);
}

export function replaceAt<T>(items: T[], index: number, value: T): T[] {
  return items.map((item, position) => (position === index ? value : item));
}

/** Strips the empty optional fields the API would reject as `undefined`. */
export function cleanMounts(mounts: Mount[]): Mount[] {
  return mounts.map((mount) => ({
    slot: mount.slot.trim(),
    region: mount.region,
    ...(mount.src?.trim() ? { src: mount.src.trim() } : {}),
  }));
}

export function cleanNav(nav: NavItem[]): NavItem[] {
  return nav.map((item) => ({ label: item.label.trim(), href: item.href.trim() }));
}

/**
 * The escape hatch: parse hand-written JSON back into the two lists.
 *
 * A form cannot express everything — a field a newer control plane understands, a bulk paste from
 * somewhere else — and an operator tool that traps you inside its own widgets is worse than one
 * that offers a way around them.
 */
export function parseRaw(text: string): { nav: NavItem[]; mounts: Mount[] } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (cause) {
    return { error: `That is not valid JSON: ${(cause as Error).message}` };
  }

  const value = parsed as { nav?: unknown; mounts?: unknown };
  if (!Array.isArray(value?.nav) || !Array.isArray(value?.mounts)) {
    return { error: 'Expected an object with "nav" and "mounts" arrays.' };
  }
  return { nav: value.nav as NavItem[], mounts: value.mounts as Mount[] };
}

export function toRaw(nav: NavItem[], mounts: Mount[]): string {
  return JSON.stringify({ nav, mounts }, null, 2);
}
